import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ArrowRight } from 'lucide-react';
import { ORG_TREE, listDepartments } from '../../data/orgTree';
import { useHGS } from '../../context/HGSContext';
import { cn } from '@/lib/utils';

/**
 * Division-wise summary for the Operating Head dashboard.
 *  - Shows ONLY divisions under the OH's domain.
 *  - Aggregates form responses + countermeasures for each division.
 */
export default function DivisionSummaryPanel() {
  const { user, formResponses, countermeasures } = useHGS();
  const navigate = useNavigate();

  const ownDomain = user?.role === 'operating_head' ? user.operatingHead : null;

  const summaries = useMemo(() => {
    if (!ownDomain || !ORG_TREE[ownDomain]) return [];
    const oh = ORG_TREE[ownDomain];
    const allDepts = listDepartments();

    return Object.values(oh.divisions).map((div) => {
      // depts in this division
      const deptIds = new Set(Object.keys(div.departments));

      // count responses by status, scoped to this division's depts
      let total = 0;
      let submitted = 0;
      let underReview = 0;
      let approved = 0;
      let finalApproved = 0;
      Object.values(formResponses || {}).forEach((r) => {
        const dept = r?.submittedDepartment;
        if (dept && deptIds.has(dept)) {
          total++;
          if (r.workflowStatus === 'Submitted') submitted++;
          else if (r.workflowStatus === 'UnderReview') underReview++;
          else if (r.workflowStatus === 'Approved') approved++;
          else if (r.workflowStatus === 'FinalApproved') finalApproved++;
        }
      });

      // countermeasures scoped by department lineage
      const myCms = (countermeasures || []).filter((c) => {
        const lineage = allDepts.find((d) => d.deptId === c.department);
        return lineage && lineage.divId === div.id;
      });
      const escalatedCount = myCms.filter((c) => c.escalated || c.daysOverdue > 30).length;
      const openCms = myCms.filter((c) => c.status !== 'Closed').length;

      // crude division compliance score
      const denom = total || 1;
      const score = total === 0 ? 100 : Math.round(((approved + finalApproved) / denom) * 100);

      return {
        id: div.id,
        label: div.label,
        head: div.head,
        deptCount: deptIds.size,
        total,
        submitted,
        underReview,
        approved,
        finalApproved,
        openCms,
        escalatedCount,
        score,
      };
    });
  }, [ownDomain, formResponses, countermeasures]);

  if (!ownDomain || summaries.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="division-summary-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-bold text-slate-900">Division-wise Summary</h3>
          <p className="text-xs text-slate-500">Compliance health across {summaries.length} divisions in {ORG_TREE[ownDomain].label}</p>
        </div>
        <button
          onClick={() => navigate('/approvals')}
          className="text-xs font-semibold text-[#CC0000] hover:underline inline-flex items-center gap-1"
        >
          View approvals <ArrowRight className="w-3 h-3" />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {summaries.map((s) => (
          <button
            key={s.id}
            data-testid={`division-card-${s.id}`}
            onClick={() => navigate('/approvals')}
            className="text-left p-4 rounded-lg border border-slate-200 hover:border-[#CC0000]/40 hover:bg-red-50/20 transition group"
          >
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-3.5 h-3.5 text-slate-500" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-slate-900 truncate">{s.label}</div>
                <div className="text-[10px] text-slate-500 truncate">{s.head?.name} · {s.deptCount} depts</div>
              </div>
              <span className={cn(
                'text-[10px] font-bold px-1.5 py-0.5 rounded tabular-nums',
                s.score >= 80 ? 'bg-emerald-100 text-emerald-700'
                  : s.score >= 50 ? 'bg-amber-100 text-amber-700'
                  : 'bg-red-100 text-[#CC0000]'
              )}>
                {s.score}%
              </span>
            </div>
            <div className="grid grid-cols-4 gap-1 text-[10px]">
              <div className="text-center p-1 rounded bg-blue-50 text-blue-700">
                <div className="font-bold tabular-nums">{s.submitted}</div>
                <div className="opacity-70">New</div>
              </div>
              <div className="text-center p-1 rounded bg-amber-50 text-amber-700">
                <div className="font-bold tabular-nums">{s.underReview}</div>
                <div className="opacity-70">Review</div>
              </div>
              <div className="text-center p-1 rounded bg-emerald-50 text-emerald-700">
                <div className="font-bold tabular-nums">{s.finalApproved}</div>
                <div className="opacity-70">Final</div>
              </div>
              <div className={cn('text-center p-1 rounded', s.escalatedCount > 0 ? 'bg-red-50 text-[#CC0000]' : 'bg-slate-50 text-slate-500')}>
                <div className="font-bold tabular-nums">{s.escalatedCount}</div>
                <div className="opacity-70">SLA</div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
